import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react'
import { ArrowUp, Paperclip, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtSize, type PendingAttachment } from '../attachments'

interface Props {
  value: string
  onChange: (next: string) => void
  onSubmit: () => void
  disabled?: boolean
  placeholder?: string
  /** Files staged for this turn (chips above the field). */
  attachments?: PendingAttachment[]
  /** User picked / dropped / pasted files. */
  onAddFiles?: (files: File[]) => void
  /** Remove a staged file by index. */
  onRemoveAttachment?: (index: number) => void
}

/**
 * Spare composer matching the concierge input's restraint. Now also a drop target: drag files
 * onto it, paste an image, or click the paperclip — staged files show as chips above the field
 * and ride with the next turn (IngestNode reads + files them). Enter submits, Shift+Enter newline.
 * A turn can be sent with files and no text.
 */
export function StewardInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
  attachments = [],
  onAddFiles,
  onRemoveAttachment,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // Auto-grow up to 200px.
  useEffect(() => {
    const ta = ref.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [value])

  const canSubmit = !disabled && (value.trim().length > 0 || attachments.length > 0)

  const submit = () => {
    if (!canSubmit) return
    onSubmit()
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (window.matchMedia('(pointer: coarse)').matches) return
      e.preventDefault()
      submit()
    }
  }

  const addFiles = (files: FileList | File[] | null | undefined) => {
    if (!files || !onAddFiles) return
    const arr = Array.from(files)
    if (arr.length) onAddFiles(arr)
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (disabled) return
    addFiles(e.dataTransfer?.files)
  }

  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files || [])
    if (files.length) {
      e.preventDefault()
      addFiles(files)
    }
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={cn(
        'rounded-lg transition-colors',
        dragOver && 'bg-foreground/[0.04] outline-2 outline-dashed outline-border'
      )}
    >
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <span
              key={`${a.filename}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-[12px] font-light text-foreground"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="max-w-[200px] truncate">{a.filename}</span>
              <span className="text-muted-foreground/70">{fmtSize(a.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveAttachment?.(i)}
                className="text-muted-foreground/60 hover:text-foreground"
                aria-label={`Remove ${a.filename}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => { addFiles(e.target.files); e.target.value = '' }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          className="flex-shrink-0 p-1 text-muted-foreground/60 transition-colors hover:text-foreground disabled:opacity-40"
          aria-label="Attach files"
        >
          <Paperclip size={18} strokeWidth={1.5} />
        </button>

        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          disabled={disabled}
          rows={1}
          placeholder={placeholder || 'Drop a thought…'}
          className={cn(
            'min-h-[24px] max-h-[200px] flex-1 resize-none border-0 bg-transparent py-1 leading-relaxed outline-none',
            'text-foreground placeholder:text-muted-foreground/60 disabled:opacity-50'
          )}
          style={{ fontSize: '16px' }}
        />

        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className={cn(
            'flex-shrink-0 p-1 transition-colors',
            canSubmit ? 'text-foreground hover:opacity-70' : 'cursor-not-allowed text-muted-foreground/30'
          )}
          aria-label="Send"
        >
          <ArrowUp size={18} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  )
}
